import { ReactNode, useState } from 'react';
import { useShiftsAssignment } from '@libs/store/shifts';
import { useRefreshSyncStore } from '@libs/store/refreshSync';
import { useNotification } from '@libs/snackbar';
import { parseError } from '@libs/api/errors';
import { AssignDialog, AssigneeButton, UnassignDialog } from '@organisms/shifts';
import { isServerError } from '@libs/api/errors/parseError';
import { getUniqueObjects } from '@libs/utils';
import { Shift } from '@libs/models/shifts';
import { assignShiftRequestApprove, assignShiftRequestDecline } from '@libs/api/shifts/assigning';
import { DeclineDialog } from '@organisms/shifts/declineDialog';
import { Box, Fade, Stack, Tooltip } from '@mui/material';
import { LoadingButton } from '@atoms/buttons';
import { FaIcon } from '@atoms/icons';
import { AvatarList } from '@molecules/dataDisplay';

type ShiftAssigneeButtonProps = {
  shift: Shift & {
    assignees: {
      id: string;
      avatar?: string;
      name: string;
      assignedByClient?: boolean;
    }[];
  };
  disabled?: boolean;
  fullWidth?: boolean;
  onAssigned?: (shiftId: string, userId: string) => void;
  onUnassigned?: (shiftId: string, reason: string, reasonComment: string) => void;
  unassignWarningMessage?: ReactNode;
  assigneeButtonProps?: {
    label?: ReactNode;
    tooltip?: ReactNode;
  };
  assignDialogProps?: {
    warningMessage?: ReactNode;
  };
  tooltip?: ReactNode;
  assignButtonVariant?: 'left' | 'center' | 'right';
};
export function ShiftAssigneeButton({
  shift,
  fullWidth,
  onAssigned,
  onUnassigned,
  disabled,
  unassignWarningMessage,
  assigneeButtonProps,
  assignDialogProps,
  assignButtonVariant = 'left',
}: ShiftAssigneeButtonProps) {
  const { assignShift, unassignShift, loadingById } = useShiftsAssignment();
  const { callRefresh } = useRefreshSyncStore();
  const { showSuccessIntl, showError, showErrorIntl } = useNotification();
  const shiftId = shift.id;
  const [opedDialog, setOpedDialog] = useState(false);
  const [opedAssignDialog, setOpedAssignDialog] = useState(false);
  const loading = loadingById[shiftId] ?? false;
  const shouldShowExternalAssignButton = shift.externalAssignRequest && shift.assignRequest?.status === 'Pending';

  const assign = async (userId: string) => {
    if (loading) return;
    await assignShift(shiftId, userId)
      .then(() => {
        callRefresh();
        showSuccessIntl('shifts.assignSuccessful');
        setOpedAssignDialog(false);
        onAssigned?.(shiftId, userId);
      })
      .catch((error: unknown) => {
        if (isServerError(error) && !error.response?.data.title) {
          showErrorIntl(`shifts.assignError.${error.response?.data.title}`);
          return;
        }
        showError(parseError(error).message);
      });
  };
  const unassign = async (reason: string, reasonComment: string) => {
    if (loading) return;
    await unassignShift(shiftId, { reason, reasonComment })
      .then(() => {
        callRefresh();
        setOpedDialog(false);
        onUnassigned?.(shiftId, reason, reasonComment);
        showSuccessIntl('shifts.unassignSuccessful');
      })
      .catch((error: unknown) => showError(parseError(error).message));
  };

  if (shouldShowExternalAssignButton && shift.assignRequest) {
    return <ExternalAssignButton shift={shift} />;
  }

  return (
    <>
      <AssigneeButton
        value={getUniqueObjects(shift.assignees)}
        disabled={disabled}
        fullWidth={fullWidth}
        loading={loading}
        onAssignClick={() => setOpedAssignDialog(true)}
        onUnassignClick={() => setOpedDialog(true)}
        alignAssigneeButtonVariant={assignButtonVariant}
        {...assigneeButtonProps}
      />
      <AssignDialog
        open={opedAssignDialog}
        shift={shift}
        onClose={() => setOpedAssignDialog(false)}
        onSubmit={assign}
        loading={loading}
        {...assignDialogProps}
      />
      <UnassignDialog
        open={opedDialog}
        shift={{
          ...shift,
          assigneeName: shift.assignees.map((a) => a.name).join(', '),
        }}
        onClose={() => setOpedDialog(false)}
        onSubmit={unassign}
        loading={loading}
        warningMessage={unassignWarningMessage}
      />
    </>
  );
}

type ExternalAssignButtonProps = {
  shift: Shift;
};
export function ExternalAssignButton({ shift }: ExternalAssignButtonProps) {
  const assignees = shift.assignRequest?.staff ? [shift.assignRequest.staff] : [];
  const [buttonsShowed, setButtonsShowed] = useState(false);
  const [loading, setLoading] = useState(false);
  const { callRefresh } = useRefreshSyncStore();
  const [opedDialog, setOpedDialog] = useState(false);
  const { showSuccessIntl, showError, showErrorIntl } = useNotification();

  const handleApprove = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    setLoading(true);
    assignShiftRequestApprove(shift.id)
      .then(() => {
        setLoading(false);
        callRefresh();
      })
      .catch((error: unknown) => {
        showError('This provider has overlapping shifts and cannot be assigned to this shift.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleReject = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    setOpedDialog(true);
  };

  const handleRejectSubmit = async (shiftId: string, reason: string) => {
    if (loading) return;
    await assignShiftRequestDecline(shiftId, reason)
      .then(() => {
        callRefresh();
        setOpedDialog(false);
      })
      .catch((error: unknown) => showError(parseError(error).message));
  };

  return (
    <Tooltip title={'External assign request pending approval'}>
      <span>
        <Stack
          direction="row"
          width="100%"
          height="100%"
          alignContent="center"
          alignItems="center"
          onMouseEnter={() => {
            setButtonsShowed(true);
          }}
          onMouseLeave={() => {
            setButtonsShowed(false);
          }}
          sx={{
            position: 'relative',
          }}
        >
          <Box
            sx={(theme) => ({
              height: '80%',
              width: '4px',
              borderRadius: '4px',
              backgroundColor: theme.palette.error.main,
            })}
          />
          <AvatarList
            list={assignees.map((v) => ({
              id: v.id,
              avatar: v.imageUrl,
              name: v.fullName || [v.firstName, v.lastName].filter(Boolean).join(' '),
            }))}
          />
          <Fade in={buttonsShowed} timeout={150}>
            <Stack direction={'row'} spacing={1} sx={{ position: 'absolute', top: 0, right: 8 }}>
              <Tooltip title={'Approve'}>
                <span>
                  <LoadingButton
                    loading={loading}
                    id="approve"
                    variant="contained"
                    color="success"
                    onClick={handleApprove}
                  >
                    <FaIcon name="check" color="background.paper" />
                  </LoadingButton>
                </span>
              </Tooltip>
              <Tooltip title={'Decline'}>
                <span>
                  <LoadingButton loading={loading} id="reject" variant="contained" color="error" onClick={handleReject}>
                    <FaIcon name="xmark" color="background.paper" />
                  </LoadingButton>
                </span>
              </Tooltip>
            </Stack>
          </Fade>
        </Stack>
        <DeclineDialog
          open={opedDialog}
          shift={{
            ...shift,
            assigneeName: assignees.map((a) => a.fullName).join(', '),
          }}
          onClose={() => setOpedDialog(false)}
          onSubmit={(reason: string) => handleRejectSubmit(shift.id, reason)}
          loading={loading}
          // warningMessage={unassignWarningMessage}
        />
      </span>
    </Tooltip>
  );
}
